import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Field, StepHeader, NavButtons } from "../common";
import { ScoreBoard } from "../ScoreBoard";
import { useSession } from "../../lib/session";

const VITALS = [["hr", "Heart rate", "bpm"], ["sbp", "Systolic BP", "mmHg"], ["dbp", "Diastolic BP", "mmHg"], ["rr", "Resp. rate", "/min"],
  ["temp", "Temperature", "°C"], ["spo2", "SpO2", "%"], ["height", "Height", "cm"], ["weight", "Weight", "kg"]];
const LABS = [["pao2", "PaO2", "mmHg"], ["fio2", "FiO2", "%"], ["paco2", "PaCO2", "mmHg"], ["ph", "Arterial pH", ""],
  ["platelets", "Platelets", "×10³/µL"], ["bilirubin", "Bilirubin", "mg/dL"], ["creatinine", "Creatinine", "mg/dL"], ["urine_output", "Urine output", "mL/day"],
  ["na", "Sodium", "mmol/L"], ["k", "Potassium", "mmol/L"], ["hct", "Hematocrit", "%"], ["wbc", "WBC", "×10³/µL"]];
const GCS = {
  gcs_e: ["Eye", ["1 None", "2 To pain", "3 To voice", "4 Spontaneous"]],
  gcs_v: ["Verbal", ["1 None", "2 Sounds", "3 Words", "4 Confused", "5 Oriented"]],
  gcs_m: ["Motor", ["1 None", "2 Extension", "3 Abn. flexion", "4 Withdraws", "5 Localises", "6 Obeys"]],
};
const VASO = [["0", "None"], ["2", "Dopamine ≤5 or any dobutamine"], ["3", "Dopa >5 / NE or Epi ≤0.1 µg/kg/min"], ["4", "Dopa >15 / NE or Epi >0.1 µg/kg/min"]];

const Sel = ({ label, value, onChange, opts, testid }) => (
  <div className="space-y-1.5">
    <div className="cc-label">{label}</div>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="cc-input"><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 border rounded-md px-4 py-3 bg-card text-sm">
    {label}<Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
  </label>
);

export default function TriageStep({ scores }) {
  const { state, set } = useSession();
  const v = state.vitals, l = state.labs;
  return (
    <div>
      <StepHeader num={3} title="Vitals & Triage" sub="Physician findings. Scores calculate instantly; missing parameters are counted as normal (partial score shown)." />
      <div className="sticky top-[104px] z-20 bg-background py-3 -mx-1 px-1"><ScoreBoard s={scores} /></div>
      <h2 className="font-heading text-lg font-semibold mt-8 mb-4">Vitals, height & weight</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {VITALS.map(([id, lb, u]) => <Field key={id} id={id} label={lb} unit={u} type="number" value={v[id]} onChange={(x) => set(`vitals.${id}`, x)} />)}
      </div>
      <h2 className="font-heading text-lg font-semibold mt-8 mb-4">Glasgow Coma Scale</h2>
      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(GCS).map(([id, [lb, opts]]) => (
          <Sel key={id} label={lb} testid={`select-${id}`} value={v[id]} onChange={(x) => set(`vitals.${id}`, x)} opts={opts.map((o) => [o[0], o])} />
        ))}
      </div>
      <Accordion type="single" collapsible defaultValue="labs" className="mt-8 border rounded-md bg-card">
        <AccordionItem value="labs" className="px-5 border-0">
          <AccordionTrigger data-testid="accordion-labs" className="font-heading text-base">Labs & support for SOFA / APACHE II (if available)</AccordionTrigger>
          <AccordionContent className="pb-6 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {LABS.map(([id, lb, u]) => <Field key={id} id={id} label={lb} unit={u} type="number" value={l[id]} onChange={(x) => set(`labs.${id}`, x)} />)}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Sel label="Vasopressor support" testid="select-vasopressor" value={l.vasopressor} onChange={(x) => set("labs.vasopressor", x)} opts={VASO} />
              <Sel label="Admission type" testid="select-admission-type" value={l.admission_type} onChange={(x) => set("labs.admission_type", x)}
                opts={[["nonoperative", "Non-operative"], ["emergency", "Emergency post-op"], ["elective", "Elective post-op"]]} />
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <Toggle label="Mechanically ventilated" testid="switch-ventilated" checked={l.ventilated} onChange={(x) => set("labs.ventilated", x)} />
              <Toggle label="Acute renal failure" testid="switch-arf" checked={l.arf} onChange={(x) => set("labs.arf", x)} />
              <Toggle label="Severe chronic organ insufficiency / immunocompromised" testid="switch-chronic" checked={l.chronic_health} onChange={(x) => set("labs.chronic_health", x)} />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <NavButtons onBack={() => set("step", 1)} onNext={() => set("step", 3)} nextLabel="Examination" />
    </div>
  );
}
